import { jsx as _jsx } from "react/jsx-runtime";
import { requiredValidator } from './formrValidators';
class FormrFieldBuilder {
    field;
    def;
    context;
    constructor(field, def, context) {
        this.field = field;
        this.def = def;
        this.context = context;
    }
    label(label) {
        this.field.label = label;
        return this;
    }
    /**
     * Set label via a translation function on the context.
     * Expects context.t to be a function: (key, options?, defaultValue?) => string
     */
    labeli18n(translationKey, options, defaultValue) {
        if (typeof this.context.t === 'function') {
            this.field.label = this.context.t(translationKey, options, defaultValue);
        }
        return this;
    }
    type(type) {
        this.field.type = type;
        this.def.configureFieldByType(this.field, type);
        return this;
    }
    required(required) {
        if (required) {
            this.field.validator = requiredValidator;
        }
        return this;
    }
    validator(validator) {
        this.field.validator = validator;
        return this;
    }
    buildField() {
        return this.def;
    }
    hidden(hiddenFn) {
        this.field.hidden = hiddenFn;
        return this;
    }
    defaultValue(value) {
        this.field.defaultValue = value;
        this.field.getter = (data) => data ?? value;
        this.field.setter = (data) => data ?? value;
        return this;
    }
    order(order) {
        this.field.order = order;
        return this;
    }
    autoFocus(autoFocus) {
        this.field.autoFocus = autoFocus;
        return this;
    }
    getter(fn) {
        this.field.getter = fn;
        return this;
    }
    setter(fn) {
        this.field.setter = fn;
        return this;
    }
    editable(editableFn) {
        this.field.editable = editableFn;
        return this;
    }
    readonly() {
        this.field.editable = () => false;
        return this;
    }
    select(options, type = 'select') {
        this.field.type = type;
        if (Array.isArray(options)) {
            this.field.options = options;
        }
        else {
            this.field.options = Object.values(options);
        }
        return this;
    }
    listView(listView) {
        this.field.listView = listView;
        return this;
    }
    viewAs(viewAs) {
        this.field.viewAs = viewAs;
        return this;
    }
    editAs(editAs) {
        this.field.editAs = editAs;
        return this;
    }
    withParentData() {
        this.field.needsParentData = true;
        return this;
    }
    asLink(urlTemplate) {
        this.field.viewAs = (data) => {
            const url = urlTemplate.replace('{value}', data);
            return (_jsx("a", { href: url, className: "text-blue-600 underline hover:text-blue-800", children: data }));
        };
        return this;
    }
}
class FormrDefBuilder {
    def;
    context;
    typeConfiguratorFn;
    currentSection;
    constructor(context, options, def) {
        this.def = def ?? { fields: new Map() };
        this.context = context;
        this.typeConfiguratorFn = options?.typeConfigurator;
    }
    configureFieldByType(field, type) {
        // Delegate to app-provided configurator for custom types
        if (this.typeConfiguratorFn) {
            this.typeConfiguratorFn(field, type, this.context);
        }
    }
    /**
     * Start a collapsible section: every field defined after this call belongs
     * to it (rendered folded by Formr) until the next `section()` or
     * `endSection()`. The label doubles as the section's heading and identity.
     */
    section(label) {
        this.currentSection = label;
        return this;
    }
    /** Return to top-level fields — fields defined after this are not folded. */
    endSection() {
        this.currentSection = undefined;
        return this;
    }
    field(key, type) {
        if (!this.def.fields.has(key)) {
            const fieldType = type || 'string';
            const newField = {
                key: key,
                label: key,
                section: this.currentSection,
                autoFocus: false,
                order: this.def.fields.size + 100,
                type: fieldType,
                defaultValue: undefined,
                hidden: () => false,
                editable: () => true,
                listView: 'largescreen',
                options: [],
                setter: (data) => data,
                getter: (data) => data,
                validator: () => {
                    return { isValid: true, message: '' };
                }
            };
            this.configureFieldByType(newField, fieldType);
            this.def.fields.set(key, newField);
        }
        return new FormrFieldBuilder(this.def.fields.get(key), this, this.context);
    }
    fieldUnsafe(key) {
        if (!this.def.fields.has(key)) {
            const newField = {
                key: key,
                label: key,
                section: this.currentSection,
                autoFocus: false,
                order: this.def.fields.size + 100,
                defaultValue: undefined,
                type: 'string',
                editable: () => true,
                hidden: () => false,
                listView: 'largescreen',
                options: [],
                setter: (data) => data,
                getter: (data) => data,
                validator: () => {
                    return { isValid: true, message: '' };
                }
            };
            this.def.fields.set(key, newField);
        }
        return new FormrFieldBuilder(this.def.fields.get(key), this, this.context);
    }
    divider(label) {
        return this.fieldUnsafe(crypto.randomUUID())
            .label(label)
            .type('divider')
            .listView('never')
            .buildField();
    }
    stringField(key) {
        return this.field(key, 'string');
    }
    textareaField(key) {
        return this.field(key, 'textarea');
    }
    numberField(key) {
        return this.field(key, 'number');
    }
    booleanField(key) {
        return this.field(key, 'boolean');
    }
    dateField(key) {
        return this.field(key, 'date');
    }
    spacetimeField(key) {
        return this.field(key, 'spacetime');
    }
    selectField(key, options) {
        return this.field(key, 'select').select(options);
    }
    multiSelectField(key, options) {
        return this.field(key, 'select_multi').select(options, 'select_multi');
    }
    markdownField(key) {
        return this.field(key, 'markdown');
    }
    imageField(key) {
        return this.field(key, 'image');
    }
    buildDef() {
        return this.def;
    }
}
const formrBuilder = (context, options) => {
    return new FormrDefBuilder(context, options);
};
const formrBuilderUnsafe = (context, options) => {
    return new FormrDefBuilder(context, options);
};
export { formrBuilder, formrBuilderUnsafe, FormrFieldBuilder, FormrDefBuilder };
//# sourceMappingURL=formrBuilder.js.map